import { ApiError } from '@repere/api-client';
import { NOM_FORMULE, type Formule } from '@repere/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRegime } from '../abonnement/regime';
import { useSession } from '../auth/session';
import { Bouton, Carte } from '../ui';

/**
 * Retours de paiement (T-031).
 *
 * **Cet écran ne décide de rien.** L'abonnement est ouvert par le webhook
 * `checkout.session.completed`, signé et reçu de serveur à serveur ; croire le retour du
 * navigateur ouvrirait la version payante à quiconque connaît cette adresse.
 *
 * Il a en revanche le droit de **demander** où en est la session dont le parent revient —
 * c'est ce que fait `GET /billing/checkout/:id`, qui interroge Stripe et applique le même
 * état par le même chemin idempotent. Sans cela, l'écran courait après le webhook et
 * affichait « merci » à côté d'un badge « version gratuite ».
 *
 * Quatre situations, et la quatrième est celle qui manquait :
 *
 * | Situation | Ce qu'on sait | Ce qu'on dit |
 * |---|---|---|
 * | Session payée | Stripe l'affirme | C'est actif, et voici ce qui change |
 * | Session en attente | Stripe la connaît, pas encore réglée | On attend, sans rien promettre |
 * | Sans identifiant de session | **rien** | On ne parle pas d'un paiement |
 * | Parcours abandonné | le parent a fermé | Rien n'a été débité |
 *
 * La troisième ligne est la correction de fond : la version précédente affirmait « votre
 * paiement a bien été transmis » à quiconque ouvrait l'adresse — un signet, un retour
 * arrière, une URL recopiée. Affirmer un paiement qu'on ne connaît pas est la seule chose
 * qu'un écran de paiement ne doit jamais faire.
 */

interface EtatSession {
  etat: 'paye' | 'en_attente' | 'indisponible';
}

/** Combien de fois on redemande avant de proposer la main au parent. Environ 20 s. */
const TENTATIVES_MAX = 8;

export function MerciAbonnement({
  sessionId,
  surRetour,
}: {
  /** Identifiant de session rapporté par Stripe. Absent = on ne sait rien d'un paiement. */
  sessionId?: string;
  surRetour?: () => void;
} = {}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const regime = useRegime();

  const abonne = regime.data !== undefined && regime.data.acces !== 'gratuit';
  const connu = sessionId !== undefined && sessionId !== '';

  const session = useQuery({
    queryKey: ['session-paiement', sessionId],
    enabled: connu && !abonne,
    // Une session inconnue ne le deviendra pas : on ne réessaie que ce qui peut changer,
    // c'est-à-dire un paiement pas encore enregistré chez Stripe.
    retry: (tentative, erreur) => !estIntrouvable(erreur) && tentative < TENTATIVES_MAX,
    // Constant plutôt qu'exponentiel : l'attente utile ici se compte en secondes, et un
    // recul qui double à chaque fois ferait patienter une minute pour rien.
    retryDelay: 2_500,
    queryFn: async () => {
      const reponse = await client.request<EtatSession>(
        `/billing/checkout/${encodeURIComponent(sessionId as string)}`,
      );
      // Une session encore impayée n'est pas une réponse finale : on la traite comme un
      // échec pour que la logique de reprise de la requête s'en charge, plutôt que de
      // réécrire une boucle d'attente à la main.
      if (reponse.etat === 'en_attente') throw new Error('en_attente');
      return reponse;
    },
  });

  // La confirmation change le régime du compte : le badge de l'en-tête et les gardes
  // d'écriture lisent la même clé, ils doivent apprendre la nouvelle en même temps.
  if (session.data?.etat === 'paye' && !abonne) {
    void queryClient.invalidateQueries({ queryKey: ['droits'] });
  }

  if (!connu) return <SansPaiement surRetour={surRetour} />;
  if (abonne || session.data?.etat === 'paye') {
    return (
      <Confirme
        surRetour={surRetour}
        // La formule vient du serveur, jamais du parcours qu'on croit avoir suivi : entre
        // le retour de Stripe et le rafraîchissement des droits, elle peut manquer. Elle
        // vaut alors `undefined`, et l'écran se tait sur l'autre parent plutôt que de lui
        // prêter le mauvais texte — c'est la même règle que pour le paiement lui-même.
        formule={regime.data?.acces === 'gratuit' ? undefined : regime.data?.formule}
        placeEnAttente={regime.data !== undefined && !regime.data.peutCouvrirCoparent}
      />
    );
  }
  if (session.data?.etat === 'indisponible' || estIntrouvable(session.error)) {
    return <Introuvable surRetour={surRetour} />;
  }
  if (session.isError) return <Tarde surRetour={surRetour} surReessayer={() => void session.refetch()} />;
  return <EnAttente />;
}

const estIntrouvable = (erreur: unknown): boolean =>
  erreur instanceof ApiError && erreur.status === 404;

// -- Les quatre visages de l'écran ------------------------------------------

function EnAttente() {
  return (
    <Etat
      ton="attente"
      icone={<Sablier />}
      titre="Nous confirmons votre paiement"
      // `aria-live` sur la zone entière : le passage de l'attente à la confirmation doit
      // être annoncé, sinon un lecteur d'écran reste sur « en cours » indéfiniment.
      vivant
    >
      <p>Cela prend quelques secondes. Vous pouvez rester sur cette page.</p>
    </Etat>
  );
}

/**
 * Ce qui vient d'être ouvert — et pour qui.
 *
 * La phrase sur l'autre parent **dépend de la formule**, et c'est tout l'objet de ce
 * découpage. Solo n'ouvre rien chez lui : le dire désamorce l'idée qu'on vient d'acheter un
 * droit sur quelqu'un. Duo, au contraire, ouvre son écriture — c'est ce qu'on a payé. Servir
 * le texte de Solo à un abonné Duo lui cache ce qu'il vient d'acheter, et lui laisse croire
 * que l'autre parent devra payer aussi.
 *
 * Quand la formule n'est pas connue — les droits n'ont pas encore été rafraîchis derrière le
 * retour de Stripe —, l'écran ne dit rien de l'autre parent. Se taire une seconde vaut mieux
 * que trancher à pile ou face entre deux phrases contraires.
 */
function Confirme({
  surRetour,
  formule,
  placeEnAttente,
}: {
  surRetour?: () => void;
  formule?: Formule;
  /** L'autre parent n'a pas encore rejoint : sous Duo, sa place est réservée, pas occupée. */
  placeEnAttente?: boolean;
}) {
  return (
    <Etat ton="succes" icone={<Coche />} titre="Votre abonnement est actif" vivant>
      <p>
        L'écriture est ouverte sur toutes les catégories du dossier : le journal, les
        dépenses, les documents, la messagerie et l'export.
      </p>

      {formule === 'duo' ? (
        <p className="retour-note">
          {placeEnAttente ? (
            <>
              L'écriture s'ouvrira aussi pour l'autre parent — c'est ce que couvre {NOM_FORMULE.duo}.
              Il n'a pas encore rejoint le dossier : sa place l'attend, et son accès s'ouvrira le
              jour où il le rejoindra.
            </>
          ) : (
            <>
              L'écriture est aussi ouverte pour l'autre parent — c'est ce que couvre{' '}
              {NOM_FORMULE.duo}.
            </>
          )}{' '}
          {/* Les trois garde-fous, redits là où le geste vient d'être fait : c'est le moment
              où l'on se demande ce qu'on a acheté sur l'autre, et la réponse est « rien ». */}
          Il n'est pas informé de qui paie, il peut prendre son propre abonnement quand il
          veut, et si le vôtre s'arrête, son accès va au bout de la période déjà payée.
        </p>
      ) : formule === undefined ? null : (
        <p className="retour-note">
          Rien ne change pour l'autre parent. Son accès est le sien, et le dossier reste
          lisible par vous deux, abonnement ou non.
        </p>
      )}

      <Bouton onClick={() => surRetour?.()}>Revenir à mes dossiers</Bouton>
    </Etat>
  );
}

function Tarde({
  surRetour,
  surReessayer,
}: {
  surRetour?: () => void;
  surReessayer?: () => void;
}) {
  return (
    <Etat ton="attente" icone={<Sablier />} titre="La confirmation tarde" vivant>
      <p>
        Votre paiement a été transmis, mais sa confirmation ne nous est pas encore parvenue.
      </p>
      <p className="retour-note">
        <strong>Inutile de payer une seconde fois.</strong> Votre abonnement s'activera de
        lui-même, même si vous fermez cette page.
      </p>
      <div className="retour-actions">
        <Bouton onClick={() => surReessayer?.()}>Vérifier à nouveau</Bouton>
        <Bouton variante="secondaire" onClick={() => surRetour?.()}>
          Revenir à mes dossiers
        </Bouton>
      </div>
    </Etat>
  );
}

/**
 * Ni paiement, ni session : quelqu'un est simplement arrivé ici.
 *
 * Un signet, un retour arrière, une adresse recopiée. Lui parler d'un paiement serait
 * inventer un fait — et l'inquiéter s'il n'a rien payé.
 */
function SansPaiement({ surRetour }: { surRetour?: () => void }) {
  return (
    <Etat ton="neutre" icone={<Information />} titre="Rien à confirmer ici">
      <p>
        Cette page confirme un paiement au retour de notre prestataire. Vous n'y arrivez pas
        d'un paiement, il n'y a donc rien à y attendre.
      </p>
      <Bouton onClick={() => surRetour?.()}>Revenir à mes dossiers</Bouton>
    </Etat>
  );
}

function Introuvable({ surRetour }: { surRetour?: () => void }) {
  return (
    <Etat ton="neutre" icone={<Information />} titre="Ce paiement ne nous est pas connu">
      <p>
        Le lien de retour ne correspond à aucun paiement de votre compte. S'il vient d'une
        autre session ou d'une autre personne, c'est normal.
      </p>
      <Bouton onClick={() => surRetour?.()}>Revenir à mes dossiers</Bouton>
    </Etat>
  );
}

export function AbonnementAbandonne({ surRetour }: { surRetour?: () => void } = {}) {
  return (
    <Etat ton="neutre" icone={<Information />} titre="Paiement interrompu">
      <p>Rien n'a été débité, et votre compte n'a pas changé.</p>
      <p className="retour-note">
        {NOM_FORMULE.link} reste ouvert sans limite de durée : le dossier se lit
        entièrement, et le calendrier de garde comme les alertes continuent de fonctionner.
      </p>
      <Bouton onClick={() => surRetour?.()}>Revenir à mes dossiers</Bouton>
    </Etat>
  );
}

// -- Habillage commun --------------------------------------------------------

/**
 * L'enveloppe des cinq états.
 *
 * Le `ton` ne porte **que** la couleur du pictogramme : le titre dit déjà ce qui se passe,
 * et un état qui ne se lirait qu'à sa teinte serait perdu pour un daltonien comme pour un
 * lecteur d'écran (règle « la couleur n'est jamais seule porteuse de sens »).
 */
function Etat({
  ton,
  icone,
  titre,
  children,
  vivant = false,
}: {
  ton: 'succes' | 'attente' | 'neutre';
  icone: React.ReactNode;
  titre: string;
  children: React.ReactNode;
  vivant?: boolean;
}) {
  return (
    <section className="retour-paiement">
      <Carte>
        <div
          className={`retour-etat ${ton}`}
          {...(vivant ? { 'aria-live': 'polite' as const } : {})}
        >
          <span className="retour-icone" aria-hidden="true">
            {icone}
          </span>
          <h1>{titre}</h1>
          {children}
        </div>
      </Carte>
    </section>
  );
}

/* Pictogrammes en SVG, jamais en emoji : une émoji change de dessin d'une plateforme à
   l'autre et ne suit pas les couleurs du thème. Traits de 1,5, comme le reste du produit. */

function Coche() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m8.5 12.2 2.4 2.4 4.6-5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Sablier() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 7.5V12l2.8 1.7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Information() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 11v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="8" r="1" fill="currentColor" />
    </svg>
  );
}
